import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useRef } from 'react'

interface EntitySelectorModalProps<T> {
  title: string
  description?: string
  onClose: () => void
  onSelect: (entity: T) => void
  entities: T[]
  isLoading: boolean
  renderItem: (entity: T, onClick: () => void) => React.ReactNode
  getEntityId: (entity: T) => string | number
  searchPlaceholder?: string
  searchValue?: string
  onSearchChange?: (search: string) => void
  emptyMessage?: string
  // Pagination props
  page?: number
  totalPages?: number
  totalCount?: number
  onPageChange?: (page: number) => void
}

function EntitySelectorModal<T>({
  title,
  description,
  onClose,
  onSelect,
  entities,
  isLoading,
  renderItem,
  getEntityId,
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearchChange,
  emptyMessage = 'No items found',
  page = 1,
  totalPages,
  totalCount,
  onPageChange,
}: EntitySelectorModalProps<T>) {
  const searchInputRef = useRef<HTMLInputElement>(null)
  const listContainerRef = useRef<HTMLDivElement>(null)

  // Focus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [])

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.classList.add('modal-locked')
    return () => {
      document.body.classList.remove('modal-locked')
    }
  }, [])

  // Scroll to top when page changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: page is intentional trigger
  useEffect(() => {
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0
    }
  }, [page])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }

  const hasPagination =
    totalPages !== undefined && totalPages > 1 && onPageChange

  return (
    <div
      className={css(styles.backdrop)}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="presentation"
    >
      <div className={css(styles.modal)}>
        <div className={css(styles.header)}>
          <h2 className={css(styles.title)}>{title}</h2>
          <button
            className={css(styles.closeButton)}
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <i className="fa-solid fa-times" />
          </button>
        </div>

        <div className={css(styles.content)}>
          {description && (
            <p className={css(styles.description)}>{description}</p>
          )}

          {onSearchChange && (
            <div className={css(styles.searchContainer)}>
              <i className={`fa-solid fa-search ${css(styles.searchIcon)}`} />
              <input
                ref={searchInputRef}
                type="text"
                className={css(styles.searchInput)}
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
              />
              {searchValue && (
                <button
                  type="button"
                  className={css(styles.clearButton)}
                  onClick={() => onSearchChange('')}
                  aria-label="Clear search"
                >
                  <i className="fa-solid fa-times" />
                </button>
              )}
            </div>
          )}

          {/* Results count */}
          {totalCount !== undefined && !isLoading && (
            <div className={css(styles.resultsCount)}>
              {totalCount} result{totalCount !== 1 ? 's' : ''} found
            </div>
          )}

          <div className={css(styles.listContainer)} ref={listContainerRef}>
            {isLoading ? (
              <div className={css(styles.loadingContainer)}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className={css(styles.skeletonItem)}>
                    <div
                      className={css(styles.skeletonLine, styles.skeletonTitle)}
                    />
                    <div
                      className={css(
                        styles.skeletonLine,
                        styles.skeletonSubtitle,
                      )}
                    />
                    <div className={css(styles.skeletonTags)}>
                      <div className={css(styles.skeletonTag)} />
                      <div className={css(styles.skeletonTag)} />
                      <div className={css(styles.skeletonTag)} />
                    </div>
                  </div>
                ))}
              </div>
            ) : entities.length === 0 ? (
              <div className={css(styles.emptyState)}>
                <i className={`fa-solid fa-inbox ${css(styles.emptyIcon)}`} />
                <p className={css(styles.emptyText)}>{emptyMessage}</p>
              </div>
            ) : (
              <div className={css(styles.entityList)}>
                {entities.map((entity) => (
                  <div key={getEntityId(entity)}>
                    {renderItem(entity, () => onSelect(entity))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={css(styles.footer)}>
          {/* Pagination */}
          {hasPagination && (
            <div className={css(styles.pagination)}>
              <button
                type="button"
                className={css(
                  styles.pageButton,
                  page <= 1 && styles.pageButtonDisabled,
                )}
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
                aria-label="Previous page"
              >
                <i className="fa-solid fa-chevron-left" />
              </button>
              <span className={css(styles.pageInfo)}>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className={css(
                  styles.pageButton,
                  page >= totalPages && styles.pageButtonDisabled,
                )}
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
                aria-label="Next page"
              >
                <i className="fa-solid fa-chevron-right" />
              </button>
            </div>
          )}

          <button
            className={css(styles.cancelButton)}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '85vh',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid #E5E7EB',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  closeButton: {
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#6B7280',
    fontSize: '16px',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: '#F3F4F6',
      color: '#111827',
    },
  },
  content: {
    padding: '24px',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  description: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    color: '#6B7280',
  },
  searchContainer: {
    position: 'relative',
    marginBottom: '12px',
    flexShrink: 0,
  },
  searchIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9CA3AF',
    fontSize: '14px',
  },
  searchInput: {
    width: '100%',
    padding: '10px 36px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    ':focus': {
      borderColor: '#3B82F6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
    '::placeholder': {
      color: '#9CA3AF',
    },
  },
  clearButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: '#9CA3AF',
    fontSize: '12px',
    borderRadius: '4px',
    ':hover': {
      color: '#6B7280',
    },
  },
  resultsCount: {
    fontSize: '12px',
    color: '#6B7280',
    marginBottom: '12px',
    flexShrink: 0,
  },
  listContainer: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  skeletonItem: {
    padding: '16px',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
  },
  skeletonLine: {
    backgroundColor: '#E5E7EB',
    borderRadius: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonTitle: {
    height: '16px',
    width: '60%',
    marginBottom: '8px',
  },
  skeletonSubtitle: {
    height: '14px',
    width: '40%',
    marginBottom: '12px',
  },
  skeletonTags: {
    display: 'flex',
    gap: '8px',
  },
  skeletonTag: {
    height: '24px',
    width: '80px',
    backgroundColor: '#E5E7EB',
    borderRadius: '4px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    color: '#6B7280',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: 0.5,
  },
  emptyText: {
    margin: 0,
    fontSize: '14px',
  },
  entityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderTop: '1px solid #E5E7EB',
    backgroundColor: '#F9FAFB',
    flexShrink: 0,
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pageButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    backgroundColor: 'white',
    color: '#374151',
    fontSize: '12px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  pageButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: 'white',
    },
  },
  pageInfo: {
    fontSize: '13px',
    color: '#6B7280',
    minWidth: '100px',
    textAlign: 'center',
  },
  cancelButton: {
    padding: '10px 20px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    backgroundColor: 'white',
    color: '#374151',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#F3F4F6',
    },
  },
})

export default EntitySelectorModal
